import { useCallback, useEffect, useMemo } from "react";
import {
  useInstantSearch,
  useInstantSearchContext,
  useSearchBox,
  useHits,
  useRefinementList,
  useRange,
  usePagination,
  useSortBy,
} from "react-instantsearch";
import { useMakeInventoryCounts } from "@/hooks/useCars";
import { ALGOLIA_INDEX_NAME } from "@/lib/algolia";
import {
  ALGOLIA_ATTRS,
  ALGOLIA_SORT_OPTIONS,
  mapAlgoliaHitToCarCard,
  sortIndexName,
} from "@workspace/algolia-contract";
import {
  ALGOLIA_REQUEST_STAMP,
  canonicalAlgoliaHitsParams,
  type AlgoliaRequestStamp,
} from "@/lib/algolia-search-request";

const EMPTY_REFINEMENTS: string[] = [];

export function useAlgoliaSearchResults() {
  const { results, status } = useInstantSearch();
  const instantSearchInstance = useInstantSearchContext();
  const { hits } = useHits();

  const cars = useMemo(() => {
    return (hits || []).map(hit => mapAlgoliaHitToCarCard(hit as unknown as Record<string, unknown>));
  }, [hits]);

  const total = results?.nbHits || 0;
  const pageSize = results?.hitsPerPage || 12;
  const page = (results?.page || 0) + 1;
  // InstantSearch starts with an artificial, idle zero-hit response. Do not
  // use results._state to identify the request: InstantSearch's index result
  // adapter rewrites that field optimistically to the live helper state.
  const hasResponse = Boolean(results && !results.__isArtificial);
  const helper = instantSearchInstance.mainIndex.getHelper();
  const helperState = helper?.state;
  const rawResult = (results as unknown as { _rawResults?: unknown[] } | undefined)?._rawResults?.[0];
  const requestStamp = rawResult && typeof rawResult === "object"
    ? (rawResult as Record<string, unknown>)[ALGOLIA_REQUEST_STAMP] as AlgoliaRequestStamp | undefined
    : undefined;
  const responseIsCurrent = hasResponse && Boolean(requestStamp) &&
    requestStamp?.indexName === helperState?.index &&
    canonicalAlgoliaHitsParams(requestStamp?.params) === canonicalAlgoliaHitsParams(helper?.getQuery());
  const isLoading = status !== "error" && (
    status === "loading" || status === "stalled" || !responseIsCurrent
  );

  return {
    data: { cars, total, page, pageSize },
    isLoading,
    responseState: hasResponse ? requestStamp?.params : undefined,
    isError: status === "error",
  };
}

export function useAlgoliaTextSearch() {
  const { query, refine } = useSearchBox();
  return { query, setQuery: refine };
}

export function useAlgoliaRentalType() {
  // Keep the three rental-type refinement-list widgets mounted so their facets
  // are always declared on the InstantSearch helper. We DON'T drive refinement
  // through their connector `.refine()` though: on the very first interaction
  // (e.g. the URL→state sync effect firing on mount, before a connector has
  // applied its own search parameters) `refine("true")` throws "Cannot refine
  // the undeclared facet …" (Sentry DRIVESHARE_V2-AP). Driving refinement via
  // `setIndexUiState` instead goes through InstantSearch's uiState→search-
  // parameters reconciliation, which declares the facet itself and can never
  // throw that error.
  useRefinementList({ attribute: ALGOLIA_ATTRS.rentalDriving });
  useRefinementList({ attribute: ALGOLIA_ATTRS.rentalChauffeured });
  useRefinementList({ attribute: ALGOLIA_ATTRS.rentalEvent });
  const { indexUiState, setIndexUiState } = useInstantSearch();

  const currentType = useMemo(() => {
    const refinements = indexUiState.refinementList || {};
    if (refinements[ALGOLIA_ATTRS.rentalDriving]?.includes("true")) return "driving";
    if (refinements[ALGOLIA_ATTRS.rentalChauffeured]?.includes("true")) return "chauffeured";
    if (refinements[ALGOLIA_ATTRS.rentalEvent]?.includes("true")) return "event";
    return undefined;
  }, [indexUiState.refinementList]);

  const setRentalType = useCallback((type: string | undefined) => {
    // Rental type is single-select: clear all three facets, then set the chosen
    // one. Declarative uiState assignment (never `.refine()`) so it applies
    // cleanly even on the first interaction after page load.
    setIndexUiState((prev) => {
      const refinementList = { ...(prev.refinementList || {}) };
      delete refinementList[ALGOLIA_ATTRS.rentalDriving];
      delete refinementList[ALGOLIA_ATTRS.rentalChauffeured];
      delete refinementList[ALGOLIA_ATTRS.rentalEvent];
      if (type === "driving") refinementList[ALGOLIA_ATTRS.rentalDriving] = ["true"];
      else if (type === "chauffeured") refinementList[ALGOLIA_ATTRS.rentalChauffeured] = ["true"];
      else if (type === "event") refinementList[ALGOLIA_ATTRS.rentalEvent] = ["true"];
      return { ...prev, refinementList };
    });
  }, [setIndexUiState]);

  return { currentType, setRentalType };
}

export function useAlgoliaMake() {
  // Use the live disjunctive facet counts so makes that match the other active
  // filters rank first. Missing values in a populated facet response have zero
  // matches; use global inventory popularity only before facets are available.
  const { items } = useRefinementList({
    attribute: ALGOLIA_ATTRS.make,
    limit: 1000,
    sortBy: ["name:asc"],
  });
  const { data, isLoading: makesLoading } = useMakeInventoryCounts();
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const currentMake = indexUiState.refinementList?.[ALGOLIA_ATTRS.make]?.[0];
  const makes = useMemo(() => {
    const inventory = new Map((data?.makes ?? []).map((item) => [item.make, items.length ? 0 : item.count]));
    for (const facet of items) inventory.set(facet.value, facet.count);
    return [...inventory].map(([make, count]) => ({ make, count }));
  }, [data?.makes, items]);
  const setMake = useCallback((make: string | undefined) => {
    setIndexUiState((prev) => {
      const refinementList = { ...(prev.refinementList || {}) };
      if (make) refinementList[ALGOLIA_ATTRS.make] = [make];
      else delete refinementList[ALGOLIA_ATTRS.make];
      return { ...prev, refinementList, page: 0 };
    });
  }, [setIndexUiState]);

  return { makes, makesLoading, currentMake, setMake };
}

export function useAlgoliaEras() {
  // Facet items are response-owned and can be empty while a request is in
  // flight or when the database fallback returns no facet counts. The UI
  // selection must instead reflect the refinement state immediately.
  const { items } = useRefinementList({ attribute: ALGOLIA_ATTRS.eras, limit: 20, operator: "or", sortBy: ['count:desc', 'name:asc'] });
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const refinedEras = indexUiState.refinementList?.[ALGOLIA_ATTRS.eras] ?? EMPTY_REFINEMENTS;
  const toggleEra = useCallback((value: string) => {
    setIndexUiState(prev => {
      const refinementList = { ...(prev.refinementList || {}) };
      const current = refinementList[ALGOLIA_ATTRS.eras] || [];
      const next = current.includes(value)
        ? current.filter(era => era !== value)
        : [...current, value];
      if (next.length) refinementList[ALGOLIA_ATTRS.eras] = next;
      else delete refinementList[ALGOLIA_ATTRS.eras];
      return { ...prev, refinementList, page: 1 };
    });
  }, [setIndexUiState]);
  return { items, refinedEras, toggleEra };
}

export function useAlgoliaCarTypes() {
  const { items } = useRefinementList({ attribute: ALGOLIA_ATTRS.carTypes, limit: 50, sortBy: ['count:desc', 'name:asc'] });
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const refinedTypes = indexUiState.refinementList?.[ALGOLIA_ATTRS.carTypes] ?? EMPTY_REFINEMENTS;

  const toggleCarType = useCallback((slug: string) => {
    setIndexUiState(prev => {
      const refinementList = { ...(prev.refinementList || {}) };
      const current = refinementList[ALGOLIA_ATTRS.carTypes] || [];
      const next = current.includes(slug) ? current.filter(type => type !== slug) : [...current, slug];
      if (next.length) refinementList[ALGOLIA_ATTRS.carTypes] = next;
      else delete refinementList[ALGOLIA_ATTRS.carTypes];
      return { ...prev, refinementList, page: 1 };
    });
  }, [setIndexUiState]);

  return { items, refinedTypes, toggleCarType };
}

/**
 * Curated multi-select Colour filter (task #1835). Uses Algolia
 * `colour_id` facet with OR semantics so picking multiple colours widens
 * the result set ("red OR blue"). Facet items expose Algolia counts so
 * the dropdown can display per-colour availability and grey out values
 * that would return zero hits given the other active filters.
 */
export function useAlgoliaColours() {
  const { items } = useRefinementList({
    attribute: ALGOLIA_ATTRS.colourId,
    operator: "or",
    limit: 100,
    sortBy: ["name:asc"],
  });

  const { indexUiState, setIndexUiState } = useInstantSearch();
  const refinedColourIds = indexUiState.refinementList?.[ALGOLIA_ATTRS.colourId] ?? EMPTY_REFINEMENTS;

  const toggleColour = useCallback((id: string) => {
    setIndexUiState(prev => {
      const refinementList = { ...(prev.refinementList || {}) };
      const current = refinementList[ALGOLIA_ATTRS.colourId] || [];
      const next = current.includes(id) ? current.filter(colour => colour !== id) : [...current, id];
      if (next.length) refinementList[ALGOLIA_ATTRS.colourId] = next;
      else delete refinementList[ALGOLIA_ATTRS.colourId];
      return { ...prev, refinementList, page: 1 };
    });
  }, [setIndexUiState]);

  // Bulk-clear used by the popover's in-dropdown "Clear" action so the
  // user can wipe all colour picks without un-checking each box.
  const clear = useCallback(() => {
    setIndexUiState(prev => {
      const refinementList = { ...(prev.refinementList || {}) };
      delete refinementList[ALGOLIA_ATTRS.colourId];
      return { ...prev, refinementList, page: 1 };
    });
  }, [setIndexUiState]);

  return { items, refinedColourIds, toggleColour, clear };
}

export function useAlgoliaFeatures() {
  // Register these as facet widgets so Algolia includes them as facets in
  // the search request and they round-trip through the routing layer.
  // We deliberately do NOT use the returned refine() — see toggleFeature
  // below for the reason.
  useRefinementList({ attribute: ALGOLIA_ATTRS.instantBook });
  useRefinementList({ attribute: ALGOLIA_ATTRS.convertible });
  useRefinementList({ attribute: ALGOLIA_ATTRS.automatic });
  // Task #1867 (S2): the Algolia document field + DB column are both
  // `has_aircon`; the legacy `air_con` refinement attribute never matched
  // any document, so toggling AC was a silent no-op. Use `has_aircon` as
  // the InstantSearch attribute, and `air_con` as the public feature key
  // (matches `featureOptions` in AlgoliaSearchPageContent and the legacy
  // shareable-URL key, see AlgoliaSearchProvider).
  useRefinementList({ attribute: ALGOLIA_ATTRS.hasAircon });

  const { indexUiState, setIndexUiState } = useInstantSearch();
  const refinementList = indexUiState.refinementList || {};
  const instantBookRefined = refinementList[ALGOLIA_ATTRS.instantBook];
  const convertibleRefined = refinementList[ALGOLIA_ATTRS.convertible];
  const automaticRefined = refinementList[ALGOLIA_ATTRS.automatic];
  const airConRefined = refinementList[ALGOLIA_ATTRS.hasAircon];

  const activeFeatures = useMemo(() => {
    const features: string[] = [];
    if (instantBookRefined?.includes("true")) features.push("instant");
    if (convertibleRefined?.includes("true")) features.push("convertible");
    if (automaticRefined?.includes("true")) features.push("automatic");
    if (airConRefined?.includes("true")) features.push("air_con");
    return features;
  }, [instantBookRefined, convertibleRefined, automaticRefined, airConRefined]);

  const toggleFeature = useCallback((feature: string) => {
    let attribute: string;
    if (feature === "instant") attribute = ALGOLIA_ATTRS.instantBook;
    else if (feature === "convertible") attribute = ALGOLIA_ATTRS.convertible;
    else if (feature === "automatic") attribute = ALGOLIA_ATTRS.automatic;
    else if (feature === "has_aircon" || feature === "air_con") attribute = ALGOLIA_ATTRS.hasAircon;
    else return;

    // Toggle by writing indexUiState directly. The previous implementation
    // called the per-attribute useRefinementList.refine("true"), which is
    // unreliable for boolean facets: when the attribute's items haven't
    // been "discovered" yet (no result has surfaced the facet), the
    // widget's internal state can clobber sibling refinements during the
    // round trip — observable as "clicking Instant Book wipes the
    // Convertible / Automatic checkboxes". Direct setIndexUiState only
    // mutates the single attribute key and leaves every other refinement
    // untouched.
    setIndexUiState(prev => {
      const next = { ...(prev.refinementList || {}) };
      const current = next[attribute] || [];
      if (current.includes("true")) {
        const filtered = current.filter(v => v !== "true");
        if (filtered.length) next[attribute] = filtered;
        else delete next[attribute];
      } else {
        next[attribute] = [...current, "true"];
      }
      return { ...prev, refinementList: next };
    });
  }, [setIndexUiState]);

  return { activeFeatures, toggleFeature };
}

/**
 * Price slider — picks the underlying numeric attribute based on the active
 * rental type so chauffeur-only listings (which lack `price_driving`) are
 * still reachable. See task #1537.
 *
 * - driving      → `price_driving`
 * - event        → `price_event`
 * - chauffeured  → `chauffeured_min_price_hourly_rate`
 * - none picked  → `filter_price_per_day` (waterfall written by the indexer)
 *
 * We always call all four `useRange` hooks to keep hook-call order stable
 * and forward to whichever one matches the active rental type. When the user
 * switches rental type, any refinement on the previously-active attribute is
 * cleared so a stale numeric filter doesn't silently keep hiding rows.
 */
export function useAlgoliaPriceRange(rentalType?: string) {
  const driving = useRange({ attribute: ALGOLIA_ATTRS.priceDriving });
  const event = useRange({ attribute: ALGOLIA_ATTRS.priceEvent });
  const chauffeured = useRange({ attribute: ALGOLIA_ATTRS.chauffeuredMinHourly });
  const waterfall = useRange({ attribute: ALGOLIA_ATTRS.filterPricePerDay });

  const active = useMemo(() => {
    if (rentalType === "driving") return { ...driving, attr: ALGOLIA_ATTRS.priceDriving };
    if (rentalType === "event") return { ...event, attr: ALGOLIA_ATTRS.priceEvent };
    if (rentalType === "chauffeured") {
      return { ...chauffeured, attr: ALGOLIA_ATTRS.chauffeuredMinHourly };
    }
    return { ...waterfall, attr: ALGOLIA_ATTRS.filterPricePerDay };
  }, [rentalType, driving, event, chauffeured, waterfall]);

  // Clear stale refinements on inactive attributes when rental type changes.
  // Without this, switching from "driving" → "chauffeured" would leave the
  // old `range.price_driving` filter active in the InstantSearch UI state
  // and silently exclude listings that should now be visible.
  useEffect(() => {
    const inactive = [
      active.attr !== ALGOLIA_ATTRS.priceDriving ? driving : null,
      active.attr !== ALGOLIA_ATTRS.priceEvent ? event : null,
      active.attr !== ALGOLIA_ATTRS.chauffeuredMinHourly ? chauffeured : null,
      active.attr !== ALGOLIA_ATTRS.filterPricePerDay ? waterfall : null,
    ].filter((r): r is NonNullable<typeof r> => r !== null);
    for (const r of inactive) {
      const lo = r.start[0];
      const hi = r.start[1];
      const isCleared = (lo === undefined || lo === -Infinity) && (hi === undefined || hi === Infinity);
      if (!isCleared) {
        r.refine([-Infinity, Infinity]);
      }
    }
  }, [active.attr, driving, event, chauffeured, waterfall]);

  const { start, range, refine } = active;
  return {
    min: start[0] !== -Infinity ? start[0] : undefined,
    max: start[1] !== Infinity ? start[1] : undefined,
    rangeMin: range.min,
    rangeMax: range.max,
    attribute: active.attr,
    setRange: (min: number | undefined, max: number | undefined) => {
      refine([
        min !== undefined ? min : -Infinity,
        max !== undefined ? max : Infinity,
      ]);
    },
  };
}

export function useAlgoliaPagination() {
  const { currentRefinement, nbPages, refine } = usePagination();
  return {
    currentPage: currentRefinement + 1,
    totalPages: nbPages,
    setPage: (page: number) => refine(page - 1),
  };
}

export function useAlgoliaSort() {
  const sortItems = ALGOLIA_SORT_OPTIONS.map(o => ({
    label: o.label,
    value: sortIndexName(ALGOLIA_INDEX_NAME, o.key),
  }));

  const { currentRefinement, refine } = useSortBy({ items: sortItems });

  const sortMap: Record<string, string> = {};
  for (const o of ALGOLIA_SORT_OPTIONS) {
    sortMap[sortIndexName(ALGOLIA_INDEX_NAME, o.key)] = o.key;
  }

  const reverseSortMap: Record<string, string> = {};
  for (const [k, v] of Object.entries(sortMap)) reverseSortMap[v] = k;

  return {
    currentSort: sortMap[currentRefinement] || "relevance",
    setSort: (sort: string) => {
      const indexName = reverseSortMap[sort] || ALGOLIA_INDEX_NAME;
      refine(indexName);
    },
  };
}
